import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, Button, ProgressBar, Alert, Modal, Badge, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Quiz,
  Question,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  HostedOpenAsyncPvPSummary,
  authService,
  pvpService,
  quizService,
} from '../services/api';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import { quizQuestionImageUrlsFromKey } from '../utils/quizQuestionImageSrc';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import {
  STOP_REQUIRED_FINDS,
  STOP_TIME_LIMIT_SECONDS,
  resolveStopLetterPointsFromValidate,
} from '../utils/stopPlayScoring';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import { useTranslation } from 'react-i18next';
import { playQuizSfxCorrect, playQuizSfxIncorrect, playWitcTypewriterKeystrokeSfx } from '../audio/quizSfx';
import { onQuizSkipClick, useBlurMobileSkipFocusOnQuestionChange } from '../utils/quizSkipButton';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
  type SoloArenaResultLine,
} from '../utils/soloArenaPublishLayout';
import { normalizePlayDifficultyLabel } from '../utils/quizPlayBandScoring';
import { useStopWasm } from '../hooks/useStopWasm';

import './shared/QuizStyles.css';
import './QuizPlayer.css';

export interface StopRevealedAnswer {
  answerId: string;
  text: string;
  textEn?: string | null;
  imageAssetKey: string;
}

export interface StopPvpFinishPayload {
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  duration: number;
  questionIdsInOrder: string[];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  stopFinishAudit?: any;
}

export interface StopQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  questions: Question[];
  startTime: Date;
  /** From POST /quiz/{id}/start (shared field name with Witc). */
  stopPlaySessionId?: string | null;
  /** Encrypted bootstrap from POST /quiz/{id}/start. */
  stopEngineBootstrapCipher?: string | null;
  onPlayAgain: () => void | Promise<void>;
  playAgainLoading?: boolean;
  suppressResultsModal?: boolean;
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
  embeddedInPvp?: boolean;
  onPvpQuitRequest?: () => void;
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: StopPvpFinishPayload) => Promise<void>;
  };
}

const StopQuizPlayer: React.FC<StopQuizPlayerProps> = ({
  quizId,
  quiz,
  questions,
  startTime,
  stopPlaySessionId,
  stopEngineBootstrapCipher,
  onPlayAgain,
  playAgainLoading = false,
  suppressResultsModal = false,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange: onArenaPublishCheckedChangeProp,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
  embeddedInPvp = false,
  onPvpQuitRequest,
  pvpAsync,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [foundCount, setFoundCount] = useState(0);
  const [letterPoints, setLetterPoints] = useState(0);
  const [revealed, setRevealed] = useState<StopRevealedAnswer[]>([]);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [hasTooManySkips, setHasTooManySkips] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [questionLocked, setQuestionLocked] = useState(false);
  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChangeProp ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;
  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);

  const { ready: wasmReady, error: wasmError, submitAnswer: wasmSubmitAnswer, buildFinishPayload } =
    useStopWasm(stopEngineBootstrapCipher ?? undefined);

  const finishQuizInFlightRef = useRef(false);
  const questionShownAtRef = useRef<number>(Date.now());
  const secondsToAnswerRef = useRef<number[]>([]);
  const stopResultsRef = useRef<SoloArenaResultLine[]>([]);
  const answersRef = useRef<Map<number, string>>(new Map());
  const answerInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const arenaPublishOffer = useMemo(
    () =>
      isQuizCompleted &&
      !hasTooManySkips &&
      questions.length > 0 &&
      correctAnswers / questions.length >= 0.7,
    [isQuizCompleted, hasTooManySkips, questions.length, correctAnswers]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    return hostedOpenSummaryForArena.openCount >= hostedOpenSummaryForArena.maxOpenRooms;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted || hasTooManySkips) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted, hasTooManySkips]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRef?.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Medium';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRef, arenaPublishCheckedRef]);

  const usePt = i18n.language?.toLowerCase().startsWith('pt');
  const q = questions[currentQuestionIndex];
  const letterLabel = q ? (usePt ? q.text : q.textEn || q.text) : '';
  const imageBase = quiz.witcImageAssetsBasePath?.trim() || '';

  const handleStopAnswerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue((prev) => {
      if (next.length > prev.length) {
        playWitcTypewriterKeystrokeSfx();
      }
      return next;
    });
  }, []);

  const handleTimeout = useCallback(() => {
    if (!questionLocked && !isQuizCompleted) {
      void advanceLetter(letterPoints, false);
    }
  }, [questionLocked, isQuizCompleted, letterPoints]);

  const { timeLeft, resetTimer, stopTimer } = useQuizTimer({
    timeLimit: STOP_TIME_LIMIT_SECONDS,
    onTimeout: handleTimeout,
    isActive:
      !isQuizCompleted &&
      !questionLocked &&
      currentQuestionIndex < questions.length &&
      !showQuitModal &&
      !showResultsModal,
  });

  useEffect(() => {
    questionShownAtRef.current = Date.now();
    resetTimer();
    setInputValue('');
    setFoundCount(0);
    setLetterPoints(0);
    setRevealed([]);
    setQuestionLocked(false);
    setWrongFlash(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, questions.length]);

  useBlurMobileSkipFocusOnQuestionChange(currentQuestionIndex);

  useEffect(() => {
    if (questionLocked || isQuizCompleted || submitting || showQuitModal || showResultsModal) return;
    const id = requestAnimationFrame(() => {
      answerInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [currentQuestionIndex, questionLocked, isQuizCompleted, submitting, showQuitModal, showResultsModal]);

  const recordSecondsForCurrentQuestion = () => {
    const elapsed = Math.min(
      STOP_TIME_LIMIT_SECONDS,
      Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
    );
    secondsToAnswerRef.current[currentQuestionIndex] = elapsed;
  };

  const advanceLetter = async (pointsEarned: number, wasSuccess: boolean) => {
    recordSecondsForCurrentQuestion();
    const merged = new Map(answersRef.current);
    merged.set(currentQuestionIndex, 'stop');
    answersRef.current = merged;
    stopResultsRef.current[currentQuestionIndex] = { pointsEarned, finalAnswerText: null };

    setScore((s) => s + pointsEarned);
    if (wasSuccess) setCorrectAnswers((c) => c + 1);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      await finishQuiz(merged);
    }
  };

  const finishQuiz = async (answersForSubmit: Map<number, string>) => {
    if (finishQuizInFlightRef.current) return;
    finishQuizInFlightRef.current = true;
    setSavingQuizAttempt(true);
    stopTimer();
    const finishedAt = new Date();
    setIsQuizCompleted(true);
    const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(duration);

    const answersArray = Array.from({ length: questions.length }, (_, i) => answersForSubmit.get(i) || 'skip');
    const responseTimes = Array.from({ length: questions.length }, (_, i) =>
      answersForSubmit.get(i) === 'skip' ? 0 : (secondsToAnswerRef.current[i] ?? STOP_TIME_LIMIT_SECONDS)
    );
    const witcResults: SoloArenaResultLine[] = questions.map((_, i) =>
      stopResultsRef.current[i] ?? { pointsEarned: 0, finalAnswerText: null }
    );
    const totalScore = witcResults.reduce((s, r) => s + r.pointsEarned, 0);
    const totalCorrect = witcResults.filter((r) => r.pointsEarned > 0).length;
    setScore(totalScore);
    setCorrectAnswers(totalCorrect);

    let stopFinishAudit;
    try {
      stopFinishAudit = buildFinishPayload() ?? undefined;
    } catch {
      stopFinishAudit = undefined;
    }
    if (!stopFinishAudit) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      setSaveAttemptError('Stop finish audit is missing. Reload the quiz and try again.');
      setSavingQuizAttempt(false);
      return;
    }

    if (pvpAsync) {
      try {
        await pvpAsync.onComplete({
          score: totalScore,
          correctAnswers: totalCorrect,
          totalQuestions: questions.length,
          duration,
          questionIdsInOrder: questions.map((qu) => qu.id),
          responseTimeSecondsPerQuestion: responseTimes,
          correctPerQuestion: witcResults.map((r) => r.pointsEarned > 0),
          stopFinishAudit,
        });
      } catch (error: any) {
        finishQuizInFlightRef.current = false;
        setIsQuizCompleted(false);
        const msg =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String((error.response.data as { message?: string }).message)
            : error instanceof Error
              ? error.message
              : 'Unknown error';
        setSaveAttemptError(`Failed to save PvP results: ${msg}`);
      } finally {
        setSavingQuizAttempt(false);
      }
      return;
    }

    try {
      const response = await quizService.saveQuizAttempt(
        {
          quizId,
          score: totalScore,
          correctAnswers: totalCorrect,
          totalQuestions: questions.length,
          duration,
          isCompleted: true,
          answers: answersArray,
          questionIdsInOrder: questions.map((qu) => qu.id),
          responseTimeSecondsPerQuestion: responseTimes,
          witcResults,
          stopFinishAudit,
          playDifficulty: 'Medium',
        },
        { pwaClient: isPwaStandalone() }
      );
      setQuizResponse(response);
      if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);
      if (response.tooManySkips) {
        setHasTooManySkips(true);
        setIsQuizCompleted(false);
      }
      if (arenaPublishSnapshotRef) {
        arenaPublishSnapshotRef.current = null;
        if (
          !response.tooManySkips &&
          questions.length > 0 &&
          totalCorrect / questions.length >= 0.7 &&
          quiz
        ) {
          arenaPublishSnapshotRef.current = {
            quizId,
            playDifficulty: 'Medium',
            roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
            score: totalScore,
            correctAnswers: totalCorrect,
            totalQuestions: questions.length,
            durationSeconds: duration,
            skippedQuestions: answersArray.filter((a) => a === 'skip').length,
            questionIdsInOrder: questions.map((qu) => qu.id),
            answerIdsPerQuestion: questions.map(() => []),
            responseTimeSecondsPerQuestion: responseTimes,
            correctPerQuestion: witcResults.map((r) => r.pointsEarned > 0),
            stopFinishAudit,
            stopResults: witcResults,
          };
        }
      }
      setArenaPublishChecked(true);
      const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
      const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
      const streakUpdate = response.loginStreakUpdated ?? null;
      const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
      if ((unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) && !response.tooManySkips) {
        setCelebrationAchievements(unlocked);
        setCelebrationCharacters(unlockedChars);
        setCelebrationLoginStreak(streakUpdate);
        setShowCelebration(true);
      } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
        /* level-up modal */
      } else {
        setShowResultsModal(true);
      }
    } catch (error: unknown) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      const msg =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String((error.response.data as { message?: string }).message)
          : error instanceof Error
            ? error.message
            : 'Unknown error';
      setSaveAttemptError(`Failed to save quiz results: ${msg}`);
    } finally {
      setSavingQuizAttempt(false);
    }
  };

  const onSubmitAnswer = async () => {
    if (submitting || savingQuizAttempt || isQuizCompleted || questionLocked || !wasmReady) return;
    const text = inputValue.trim();
    if (!text || !q) return;

    setSubmitting(true);
    try {
      const res = await wasmSubmitAnswer(q.id, text);
      if (!res) {
        throw new Error('Stop engine is not ready.');
      }

      setFoundCount(res.foundCount);
      setLetterPoints(resolveStopLetterPointsFromValidate(res));

      if (res.revealedAnswer) {
        const revealedAnswer: StopRevealedAnswer = {
          answerId: res.revealedAnswer.answerId,
          text: res.revealedAnswer.text,
          textEn: res.revealedAnswer.textEn,
          imageAssetKey: res.revealedAnswer.imageAssetKey,
        };
        setRevealed((prev) => {
          if (prev.some((r) => r.answerId === revealedAnswer.answerId)) return prev;
          return [...prev, revealedAnswer];
        });
      }

      if (res.correct && !res.duplicate) {
        playQuizSfxCorrect();
      } else {
        playQuizSfxIncorrect();
        setWrongFlash(true);
        setTimeout(() => setWrongFlash(false), 650);
      }

      setInputValue('');

      if (res.questionComplete) {
        setQuestionLocked(true);
        const pts = res.pointsEarned ?? res.questionPointsSoFar;
        setTimeout(() => {
          void advanceLetter(pts, true);
          setSubmitting(false);
        }, 800);
        return;
      }

      if (res.questionFailed) {
        setQuestionLocked(true);
        const pts = res.pointsEarned ?? res.questionPointsSoFar;
        setTimeout(() => {
          void advanceLetter(pts, false);
          setSubmitting(false);
        }, 600);
        return;
      }
    } catch {
      playQuizSfxIncorrect();
      setWrongFlash(true);
      setInputValue('');
      setTimeout(() => setWrongFlash(false), 650);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipQuestion = () => {
    if (savingQuizAttempt || questionLocked) return;
    recordSecondsForCurrentQuestion();
    const merged = new Map(answersRef.current);
    merged.set(currentQuestionIndex, 'skip');
    answersRef.current = merged;
    stopResultsRef.current[currentQuestionIndex] = { pointsEarned: 0, finalAnswerText: null };

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      void finishQuiz(merged);
    }
  };

  const handleQuitQuiz = () => {
    if (embeddedInPvp && onPvpQuitRequest) {
      onPvpQuitRequest();
      return;
    }
    setShowQuitModal(true);
  };

  const confirmQuit = () => {
    stopTimer();
    setShowQuitModal(false);
    if (onPvpQuitRequest) {
      onPvpQuitRequest();
    } else {
      const quitAt = new Date();
      const secs = Math.max(0, Math.floor((quitAt.getTime() - startTime.getTime()) / 1000));
      setResultDurationSeconds(secs);
      setIsQuizCompleted(false);
      setShowResultsModal(true);
    }
  };

  const handleBackToHome = async () => {
    await executeArenaPublishFromSoloIfNeeded();
    setShowResultsModal(false);
    setShowCelebration(false);
    levelUpCelebration.clearPendingLevelUp();
    navigate('/', { replace: true });
  };

  const handleCelebrationFinished = () => {
    setShowCelebration(false);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  };

  const handleLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => setCelebrationPlayerNick((u.nickname && u.nickname.trim()) || u.name || ''))
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const progressPercent =
    questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  type StopAnswerSlotTile = StopRevealedAnswer & {
    urls: ReturnType<typeof quizQuestionImageUrlsFromKey>;
    label: string;
  };

  const answerSlots = useMemo(() => {
    const tiles: StopAnswerSlotTile[] = revealed.map((r) => {
      const urls = quizQuestionImageUrlsFromKey(imageBase, r.imageAssetKey);
      const label = usePt ? r.text : r.textEn?.trim() || r.text;
      return { ...r, urls, label };
    });
    return Array.from({ length: STOP_REQUIRED_FINDS }, (_, i) => tiles[i] ?? null);
  }, [revealed, imageBase, usePt]);

  if (wasmError) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{wasmError}</p>
          <Button variant="outline-danger" onClick={() => navigate('/', { replace: true })}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  if (!wasmReady) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  if (saveAttemptError) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{saveAttemptError}</p>
          <Button variant="outline-danger" onClick={handleBackToHome}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  if (!q || questions.length === 0) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  const stopPlayBody = (
    <div className={embeddedInPvp ? 'stop-quiz-player-embedded stop-quiz-player' : 'quiz-container'}>
      <div className="quiz-header quiz-page-header">
        {!embeddedInPvp && <QuizPlayPageHeading quiz={quiz} language={i18n.language} />}
        <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
            <Badge bg="warning">{t('quiz.medium', 'Medium')}</Badge>
            <span className="text-muted">
              {t('quiz.questionOf', 'Question {{current}} of {{total}}')
                .replace('{{current}}', String(currentQuestionIndex + 1))
                .replace('{{total}}', String(questions.length))}
            </span>
            <Badge bg={timeLeft <= 10 ? 'danger' : 'warning'} className="timer-badge">
              {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {!embeddedInPvp && <NavbarMusicToggle />}
            {!embeddedInPvp && <QuizSoundToggleButton disabled={savingQuizAttempt} />}
            <Button
              type="button"
              variant="outline-danger"
              onClick={handleQuitQuiz}
              className="quiz-quit-icon-btn"
              aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
            >
              <i className="bi bi-x-lg" aria-hidden />
            </Button>
          </div>
        </div>
      </div>

      <ProgressBar now={progressPercent} className="quiz-progress-bar" label={`${Math.round(progressPercent)}%`} />

      <Card className="question-card quiz-compact mb-3">
        <Card.Body>
          <Card.Title className="question-title h5">
            {t('stop.findCharactersHint', 'Find {{required}} characters with the letter:').replace(
              '{{required}}',
              String(STOP_REQUIRED_FINDS)
            )}
          </Card.Title>
          <div className="stop-play-area position-relative mb-3">
            <Button
              type="button"
              variant="light"
              size="sm"
              onClick={onQuizSkipClick(handleSkipQuestion)}
              disabled={questionLocked || savingQuizAttempt}
              className="witc-skip-on-image-btn d-md-none"
              aria-label={t('quiz.skipQuestionBtn', 'Skip Question')}
              title={t('quiz.skipQuestionBtn', 'Skip Question')}
            >
              <i className="bi bi-skip-forward" aria-hidden />
            </Button>
            <div className="text-center">
              <div
                className="display-1 fw-bold text-primary mb-2"
                style={{ lineHeight: 1 }}
                aria-label={t('stop.letterLabel', 'Letter')}
              >
                {letterLabel}
              </div>
              <p className="small mb-0 fw-medium text-success">
                {t('stop.pointsOnLetter', 'Points this letter: {{pts}}').replace('{{pts}}', String(letterPoints))}
              </p>
            </div>

            <div
              className="stop-answer-slots"
              aria-label={t('stop.answerSlots', 'Answer slots')}
            >
              {answerSlots.map((item, slotIndex) => (
                <div
                  key={item?.answerId ?? 'slot-' + slotIndex}
                  className={`stop-answer-slot border rounded p-2 text-center bg-white${
                    item ? ' stop-answer-slot--revealed' : ''
                  }`}
                >
                  {item ? (
                    <>
                      {item.urls ? (
                        <div className="stop-answer-slot__img-frame">
                          <img
                            src={item.urls.webp}
                            alt=""
                            className="stop-answer-slot__img img-fluid rounded d-block mx-auto"
                            onError={(e) => {
                              const img = e.currentTarget;
                              if (item.urls && img.src !== item.urls.png) img.src = item.urls.png;
                            }}
                          />
                        </div>
                      ) : null}
                      <div className="stop-answer-slot__label fw-semibold text-truncate">{item.label}</div>
                    </>
                  ) : (
                    <div className="stop-answer-slot__placeholder" aria-hidden>
                      <span className="stop-answer-slot__question">?</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmitAnswer();
            }}
          >
            <div className="d-flex flex-row flex-nowrap gap-2 align-items-end w-100">
              <Form.Control
                ref={answerInputRef}
                type="text"
                value={inputValue}
                onChange={handleStopAnswerChange}
                placeholder={t('stop.typeAnswerPlaceholder', 'Type a name…')}
                disabled={submitting || savingQuizAttempt || questionLocked}
                className={`witc-answer-input flex-grow-1 min-w-0 ${wrongFlash ? 'witc-answer-input--flash' : ''}`}
              />
              <Button type="submit" variant="primary" disabled={submitting || questionLocked || !inputValue.trim()}>
                {submitting ? <Spinner size="sm" animation="border" /> : t('witc.send', 'Send')}
              </Button>
            </div>
          </Form>

          <div className="quiz-stats-actions-container">
            <div className="quiz-stats-inline">
              <div className="stat-line">
                {t('quiz.score', 'Score')}: {score}
              </div>
            </div>
            <div className="quiz-actions quiz-footer-actions">
              <Button
                variant="outline-secondary"
                onClick={onQuizSkipClick(handleSkipQuestion)}
                className="quiz-action-btn d-none d-md-inline-flex align-items-center"
                disabled={questionLocked || savingQuizAttempt}
              >
                <i className="bi bi-skip-forward me-1 me-sm-2" aria-hidden />
                {t('quiz.skipQuestionBtn', 'Skip Question')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  if (embeddedInPvp) {
    return stopPlayBody;
  }

  return (
    <div className="quiz-page">
      {stopPlayBody}

      {savingQuizAttempt && (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      )}

      <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.quitQuizModal', 'Quit Quiz?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('quiz.quitQuizBody', 'Your progress will be lost.')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmQuit}>
            {t('quiz.quitConfirm', 'Quit')}
          </Button>
        </Modal.Footer>
      </Modal>

      {!suppressResultsModal && (
        <SoloQuizResultsModal
          show={showResultsModal && !suppressResultsModal}
          hasTooManySkips={hasTooManySkips}
          isQuizCompleted={isQuizCompleted}
          quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
          correctAnswers={correctAnswers}
          totalQuestions={questions.length}
          score={score}
          earnedXP={earnedXP}
          quizResponse={quizResponse}
          resultDurationSeconds={resultDurationSeconds}
          onPlayAgain={onPlayAgain}
          onBackToHome={() => void handleBackToHome()}
          playAgainLoading={playAgainLoading}
          arenaPublishOffer={arenaPublishOffer}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
          arenaPublishBlocked={arenaPublishBlocked}
          arenaPublishBlockedReason={arenaPublishBlockedReason}
          arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
        />
      )}

      <QuizFinishCelebrationModal
        show={showCelebration}
        achievements={celebrationAchievements}
        characters={celebrationCharacters}
        loginStreak={celebrationLoginStreak}
        playerNickname={celebrationPlayerNick}
        onFinished={handleCelebrationFinished}
      />

      <LevelUpCelebrationModal
        show={levelUpCelebration.showLevelUp}
        oldLevel={levelUpCelebration.levelUpOld}
        newLevel={levelUpCelebration.levelUpNew}
        onClose={handleLevelUpFinished}
      />
    </div>
  );
};

export default StopQuizPlayer;
